import { useEffect, useState } from "react";
import { fetchSettings, saveSettings } from "../smsApi";
import type { BusinessSchedule, SmsSettings } from "../types";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DEFAULT_SCHEDULE: BusinessSchedule = {
  "0": null,
  "1": { open: "09:00", close: "17:00" },
  "2": { open: "09:00", close: "17:00" },
  "3": { open: "09:00", close: "17:00" },
  "4": { open: "09:00", close: "17:00" },
  "5": { open: "09:00", close: "17:00" },
  "6": null,
};

const DEFAULT_SETTINGS: SmsSettings = {
  id: 1,
  auto_responder_enabled: false,
  business_schedule: DEFAULT_SCHEDULE,
  timezone: "America/New_York",
  auto_response_message:
    "Thanks for contacting Tribal Axe! We are currently closed and will respond during business hours.",
  canned_messages: [],
};

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
];

type Props = {
  isAdmin: boolean;
};

// Migrate legacy `business_days[]` + `business_hours_start/end` into the
// nested `business_schedule` the UI renders. The worker already stores the
// canonical shape, so this only kicks in for rows created before the port.
function normalizeSettings(s: Partial<SmsSettings>): SmsSettings {
  const merged: SmsSettings = { ...DEFAULT_SETTINGS, ...s };
  if (!s.business_schedule && s.business_days) {
    const sched: BusinessSchedule = {};
    for (let d = 0; d <= 6; d++) {
      sched[String(d)] = s.business_days.includes(d)
        ? {
            open: s.business_hours_start || "09:00",
            close: s.business_hours_end || "17:00",
          }
        : null;
    }
    merged.business_schedule = sched;
  }
  if (!merged.business_schedule) merged.business_schedule = DEFAULT_SCHEDULE;
  if (!merged.canned_messages) merged.canned_messages = [];
  return merged;
}

export function SettingsView({ isAdmin }: Props) {
  const readOnly = !isAdmin;
  const [settings, setSettings] = useState<SmsSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [newCanned, setNewCanned] = useState("");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  useEffect(() => {
    let alive = true;
    fetchSettings()
      .then((data) => {
        if (!alive) return;
        if (data && Object.keys(data).length > 0) {
          setSettings(normalizeSettings(data));
        }
      })
      .catch((e: Error) => {
        if (alive) setLoadError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function handleSave() {
    if (readOnly) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const out = await saveSettings({
        auto_responder_enabled: settings.auto_responder_enabled,
        business_schedule: settings.business_schedule,
        timezone: settings.timezone,
        auto_response_message: settings.auto_response_message,
        canned_messages: settings.canned_messages,
      });
      setSettings(normalizeSettings(out));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function toggleDay(day: number) {
    if (readOnly) return;
    setSettings((s) => {
      const sched: BusinessSchedule = { ...(s.business_schedule ?? {}) };
      const key = String(day);
      sched[key] = sched[key] ? null : { open: "09:00", close: "17:00" };
      return { ...s, business_schedule: sched };
    });
  }

  function updateDayHours(day: number, field: "open" | "close", value: string) {
    if (readOnly) return;
    setSettings((s) => {
      const sched: BusinessSchedule = { ...(s.business_schedule ?? {}) };
      const key = String(day);
      const current = sched[key] ?? { open: "09:00", close: "17:00" };
      sched[key] = { ...current, [field]: value };
      return { ...s, business_schedule: sched };
    });
  }

  function addCanned() {
    if (readOnly) return;
    const msg = newCanned.trim();
    if (!msg) return;
    setSettings((s) => ({
      ...s,
      canned_messages: [...(s.canned_messages ?? []), msg],
    }));
    setNewCanned("");
  }

  function deleteCanned(idx: number) {
    if (readOnly) return;
    setSettings((s) => ({
      ...s,
      canned_messages: (s.canned_messages ?? []).filter((_, i) => i !== idx),
    }));
  }

  function startEdit(idx: number) {
    if (readOnly) return;
    setEditingIdx(idx);
    setEditingText(settings.canned_messages?.[idx] ?? "");
  }

  function saveEdit() {
    const msg = editingText.trim();
    if (!msg || editingIdx === null) return;
    setSettings((s) => ({
      ...s,
      canned_messages: (s.canned_messages ?? []).map((m, i) =>
        i === editingIdx ? msg : m
      ),
    }));
    setEditingIdx(null);
    setEditingText("");
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
        <div className="animate-pulse">Loading settings…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="m-4 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
        <p className="font-medium">Failed to load settings</p>
        <p className="mt-1 font-mono text-xs">{loadError}</p>
      </div>
    );
  }

  const cannedMessages = settings.canned_messages ?? [];
  const autoMessage = settings.auto_response_message ?? "";

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl space-y-8 px-4 py-6">
        {readOnly && (
          <div className="rounded-lg border border-tribal-border bg-tribal-surface px-4 py-3 text-sm text-tribal-muted">
            You have read-only access. Contact an admin to make changes.
          </div>
        )}

        <section
          className={`overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface ${
            readOnly ? "opacity-70" : ""
          }`}
        >
          <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-white">Auto-Responder</h2>
              <p className="mt-0.5 text-xs text-tribal-muted">
                Automatically reply to inbound messages outside business hours
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                !readOnly &&
                setSettings((s) => ({
                  ...s,
                  auto_responder_enabled: !s.auto_responder_enabled,
                }))
              }
              disabled={readOnly}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                settings.auto_responder_enabled
                  ? "bg-tribal-red"
                  : "bg-tribal-border"
              } ${readOnly ? "cursor-not-allowed" : ""}`}
              aria-pressed={settings.auto_responder_enabled}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  settings.auto_responder_enabled
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="space-y-5 px-5 py-4">
            <div>
              <label className="mb-2 block text-xs font-medium text-tribal-muted">
                Business Hours
              </label>
              <div className="space-y-2">
                {DAY_LABELS.map((label, idx) => {
                  const daySchedule = settings.business_schedule?.[String(idx)];
                  const isOpen = !!daySchedule;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => toggleDay(idx)}
                        disabled={readOnly}
                        className={`w-10 shrink-0 rounded-lg px-1 py-1.5 text-center text-xs font-semibold transition-colors ${
                          isOpen
                            ? "bg-tribal-red text-white"
                            : "bg-tribal-border text-tribal-muted hover:text-gray-300"
                        } ${readOnly ? "cursor-not-allowed" : ""}`}
                      >
                        {label}
                      </button>
                      {isOpen ? (
                        <div className="flex flex-1 items-center gap-2">
                          <input
                            type="time"
                            value={daySchedule.open}
                            onChange={(e) =>
                              updateDayHours(idx, "open", e.target.value)
                            }
                            disabled={readOnly}
                            className="w-32 rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                          />
                          <span className="text-xs text-tribal-muted">to</span>
                          <input
                            type="time"
                            value={daySchedule.close}
                            onChange={(e) =>
                              updateDayHours(idx, "close", e.target.value)
                            }
                            disabled={readOnly}
                            className="w-32 rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                          />
                        </div>
                      ) : (
                        <span className="text-xs italic text-tribal-muted">
                          Closed
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-tribal-muted">
                Timezone
              </label>
              <select
                value={settings.timezone ?? "America/New_York"}
                onChange={(e) =>
                  !readOnly &&
                  setSettings((s) => ({ ...s, timezone: e.target.value }))
                }
                disabled={readOnly}
                className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-tribal-muted">
                Auto-Response Message
              </label>
              <textarea
                value={autoMessage}
                onChange={(e) =>
                  !readOnly &&
                  setSettings((s) => ({
                    ...s,
                    auto_response_message: e.target.value,
                  }))
                }
                disabled={readOnly}
                rows={3}
                maxLength={320}
                className="w-full resize-none rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
              />
              <p className="mt-0.5 text-right text-[10px] text-tribal-muted">
                {autoMessage.length}/320
              </p>
            </div>
          </div>
        </section>

        <section
          className={`overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface ${
            readOnly ? "opacity-70" : ""
          }`}
        >
          <div className="border-b border-tribal-border px-5 py-4">
            <h2 className="text-base font-semibold text-white">Canned Messages</h2>
            <p className="mt-0.5 text-xs text-tribal-muted">
              Quick replies available in the compose area and message thread.
            </p>
          </div>
          <div className="space-y-3 px-5 py-4">
            {cannedMessages.length === 0 && (
              <p className="text-sm italic text-tribal-muted">
                No canned messages yet.
              </p>
            )}
            {cannedMessages.map((msg, idx) => (
              <div key={idx} className="flex items-start gap-2">
                {editingIdx === idx ? (
                  <>
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      rows={2}
                      className="flex-1 resize-none rounded-lg border border-tribal-red bg-tribal-bg px-3 py-2 text-sm text-white focus:outline-none"
                      autoFocus
                    />
                    <div className="flex flex-shrink-0 flex-col gap-1">
                      <button
                        type="button"
                        onClick={saveEdit}
                        className="rounded-lg bg-tribal-red px-3 py-1.5 text-xs text-white transition-colors hover:bg-tribal-red-dark"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingIdx(null)}
                        className="rounded-lg bg-tribal-border px-3 py-1.5 text-xs text-gray-300 transition-colors hover:bg-white/10"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="flex-1 rounded-lg bg-tribal-bg px-3 py-2 text-sm text-gray-200">
                      {msg}
                    </p>
                    {!readOnly && (
                      <div className="flex flex-shrink-0 flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(idx)}
                          className="rounded-lg bg-tribal-border px-3 py-1.5 text-xs text-gray-300 transition-colors hover:bg-white/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteCanned(idx)}
                          className="rounded-lg bg-tribal-border px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-white/10"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            {!readOnly && (
              <div className="flex gap-2 pt-1">
                <input
                  type="text"
                  value={newCanned}
                  onChange={(e) => setNewCanned(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCanned()}
                  placeholder="Add a quick reply…"
                  className="flex-1 rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                />
                <button
                  type="button"
                  onClick={addCanned}
                  disabled={!newCanned.trim()}
                  className="rounded-lg bg-tribal-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </section>

        {!readOnly && (
          <div className="flex items-center justify-end gap-3 pb-8">
            {saveError && (
              <span className="rounded-lg bg-red-500/15 px-3 py-1.5 text-sm text-red-300">
                {saveError}
              </span>
            )}
            {saved && <span className="text-sm text-green-400">Settings saved.</span>}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-tribal-red px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save SMS Settings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
